import React, { useRef, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Button,
  Icon,
  Input,
  CheckboxV2,
  LinkButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  menuItemRowInteractiveClasses,
  menuItemLabelClasses,
  menuItemEmphasisLabelClasses,
} from '@shopflo/ui'

import { CaretLeft, CaretRight, Funnel, MagnifyingGlass, CircleNotch } from '@phosphor-icons/react'
import { useFilterDropdown } from './useFilterDropdown'

/** Matches `[data-submenu-option-row]` / `.submenu-options-scroll` selectors in option-row classes. */
const SUBMENU_OPTIONS_SCROLL = 'submenu-options-scroll'

const SUBMENU_OPTION_ROW_BTN =
  `${menuItemRowInteractiveClasses} focus:outline-none focus-visible:outline-none focus-visible:ring-0 first:bg-zinc-50 [.submenu-options-scroll:has([data-submenu-option-row]:focus-visible)_&]:not(:focus-visible):hover:bg-transparent`

const SUBMENU_OPTION_ROW_LABEL_SELECT_ALL =
  `${menuItemEmphasisLabelClasses} [.submenu-options-scroll:has([data-submenu-option-row]:focus-visible)_button:not(:focus-visible):hover_&]:text-zinc-700`

const SUBMENU_OPTION_ROW_LABEL_OPTION =
  `${menuItemLabelClasses} [.submenu-options-scroll:has([data-submenu-option-row]:focus-visible)_button:not(:focus-visible):hover_&]:text-zinc-600`

/** Main filter category list (first screen) — same `has()` / `data-*` pattern as submenu option rows. */
const MAIN_FILTER_LIST_SCROLL = 'main-filter-list-scroll'

const MAIN_FILTER_CATEGORY_BTN =
  `${menuItemRowInteractiveClasses} justify-between hover:[&_svg]:text-zinc-600 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:[&_svg]:text-zinc-600 first:bg-zinc-50 first:text-zinc-800 first:[&_svg]:text-zinc-600 [.main-filter-list-scroll:has([data-main-filter-category-row]:focus-visible)_&]:not(:focus-visible):hover:bg-transparent [.main-filter-list-scroll:has([data-main-filter-category-row]:focus-visible)_&]:not(:focus-visible):hover:text-zinc-600 [.main-filter-list-scroll:has([data-main-filter-category-row]:focus-visible)_&]:not(:focus-visible):hover:[&_svg]:text-zinc-400`

const MAIN_FILTER_CATEGORY_LABEL =
  `${menuItemLabelClasses} text-left [.main-filter-list-scroll:has([data-main-filter-category-row]:focus-visible)_button:not(:focus-visible):hover_&]:text-zinc-600`

export type RangeValue = { minPrice: string; maxPrice: string }

export type FilterOption<TValue> = {
  label: string
  value: TValue
  count?: number
  dotColor?: string
}

export type FilterConfig<TValue> = {
  label: string
  options?: Array<FilterOption<TValue>>
  isRangeFilter?: boolean
  rangeInputMode?: 'dual' | 'select'
  rangeSelectOptions?: Array<{ label: string; value: keyof RangeValue }>
  rangeMin?: number
  rangeMax?: number
  isSearchFilter?: boolean
  searchPlaceholder?: string
}

export interface FilterDropdownProps<TFilterType extends string, TValue> {
  enableFilter: boolean
  filterConfigMap: Partial<Record<TFilterType, FilterConfig<TValue>>>

  isOptionChecked?: (filterType: TFilterType, value: TValue) => boolean
  onOptionsApply?: (filterType: TFilterType, values: TValue[]) => void
  onOptionToggle?: (filterType: TFilterType, value: TValue) => void

  getRangeInitialValue?: (filterType: TFilterType) => RangeValue
  onRangeApply?: (filterType: TFilterType, range: RangeValue) => void

  getSearchInitialValue?: (filterType: TFilterType) => { selectedOption?: TValue; searchText: string }
  onSearchApply?: (filterType: TFilterType, selectedOption: TValue, searchText: string) => void

  triggerLabel?: string
  triggerClassName?: string
  triggerActive?: boolean
  currencySymbol?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  openWithSubMenu?: TFilterType | null
  isApplying?: boolean
  trigger?: React.ReactNode
  /** Shown on hover when `trigger` is a custom element. Radix: TooltipTrigger wraps PopoverTrigger. */
  triggerTooltip?: React.ReactNode
  appliedFilterCount?: number
  /** Popover alignment for the filter dropdown (main list and sub-menus). */
  mainMenuAlign?: 'start' | 'center' | 'end'
}

export function FilterDropdown<TFilterType extends string, TValue>({
  enableFilter,
  filterConfigMap,
  isOptionChecked,
  onOptionsApply,
  onOptionToggle,
  getRangeInitialValue,
  onRangeApply,
  getSearchInitialValue,
  onSearchApply,
  triggerLabel = 'Filters',
  triggerClassName = 'w-fit whitespace-nowrap h-9 bg-white px-3 flex items-center gap-1 border-[#e4e4e7]',
  triggerActive = false,
  currencySymbol = '₹',
  open: controlledOpen,
  onOpenChange: onControlledOpenChange,
  openWithSubMenu,
  isApplying = false,
  trigger: customTrigger,
  triggerTooltip,
  appliedFilterCount,
  mainMenuAlign = 'start',
}: FilterDropdownProps<TFilterType, TValue>) {
  const {
    open,
    handleOpenChange,
    filterSearchText,
    setFilterSearchText,
    subMenuSearchText,
    setSubMenuSearchText,
    activeSubMenu,
    activeSubMenuConfig,
    filteredFilterConfig,
    filteredSubMenuOptions,
    activeRangeValue,
    activeSearchFilterValue,
    effectivePendingSelections,
    rangeSelectedField,
    rangeValidationError,
    handleCategoryClick,
    handleBackClick,
    togglePendingOption,
    toggleSelectAllFilteredOptions,
    handleRangeFieldChange,
    handleRangeSelectFieldChange,
    handleSearchOptionChange,
    handleSearchTextChange,
    handleApply,
    flushPendingApply,
  } = useFilterDropdown({
    filterConfigMap,
    isOptionChecked,
    onOptionsApply,
    onOptionToggle,
    getRangeInitialValue,
    onRangeApply,
    getSearchInitialValue,
    onSearchApply,
    currencySymbol,
    controlledOpen,
    onControlledOpenChange,
    openWithSubMenu,
  })

  const [isListAtBottom, setIsListAtBottom] = useState(false)

  const checkIsAtBottom = (el: HTMLDivElement) =>
    Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight

  const handleSubMenuScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsListAtBottom(checkIsAtBottom(e.currentTarget))
  }

  const mainFilterListRef = useRef<HTMLDivElement | null>(null)
  const mainListSearchInputRef = useRef<HTMLInputElement>(null)
  const optionsListRef = useRef<HTMLDivElement | null>(null)
  const subMenuSearchInputRef = useRef<HTMLInputElement>(null)

  const mainFilterListRefCallback = (el: HTMLDivElement | null) => {
    mainFilterListRef.current = el
  }

  const subMenuListRefCallback = (el: HTMLDivElement | null) => {
    optionsListRef.current = el
    if (el) setIsListAtBottom(checkIsAtBottom(el))
  }

  const getMainFilterCategoryRows = () =>
    mainFilterListRef.current
      ? Array.from(
          mainFilterListRef.current.querySelectorAll<HTMLButtonElement>('[data-main-filter-category-row]')
        )
      : []

  const focusFirstMainFilterCategoryRow = () => {
    getMainFilterCategoryRows()[0]?.focus()
  }

  const handleMainListSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      focusFirstMainFilterCategoryRow()
    }
  }

  const handleMainFilterListPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    const prev = e.relatedTarget
    if (prev instanceof Node && e.currentTarget.contains(prev)) return
    if (document.activeElement === mainListSearchInputRef.current) return
    focusFirstMainFilterCategoryRow()
  }

  const handleMainFilterListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target
    if (!(target instanceof HTMLButtonElement) || !target.hasAttribute('data-main-filter-category-row')) return

    const rows = getMainFilterCategoryRows()
    const i = rows.indexOf(target)
    if (i === -1) return

    if (e.key === 'ArrowDown') {
      if (i < rows.length - 1) {
        e.preventDefault()
        rows[i + 1]?.focus()
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (i === 0) {
        mainListSearchInputRef.current?.focus()
      } else {
        rows[i - 1]?.focus()
      }
    }
  }

  const getSubmenuOptionRows = () =>
    optionsListRef.current
      ? Array.from(
          optionsListRef.current.querySelectorAll<HTMLButtonElement>('[data-submenu-option-row]')
        )
      : []

  const focusFirstSubmenuOptionRow = () => {
    getSubmenuOptionRows()[0]?.focus()
  }

  const handleSubmenuSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      focusFirstSubmenuOptionRow()
    }
  }

  const handleOptionsListPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    const prev = e.relatedTarget
    if (prev instanceof Node && e.currentTarget.contains(prev)) return
    if (document.activeElement === subMenuSearchInputRef.current) return
    focusFirstSubmenuOptionRow()
  }

  const handleOptionsListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target
    if (!(target instanceof HTMLButtonElement) || !target.hasAttribute('data-submenu-option-row')) return

    const rows = getSubmenuOptionRows()
    const i = rows.indexOf(target)
    if (i === -1) return

    if (e.key === 'ArrowDown') {
      if (i < rows.length - 1) {
        e.preventDefault()
        rows[i + 1]?.focus()
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (i === 0) {
        subMenuSearchInputRef.current?.focus()
      } else {
        rows[i - 1]?.focus()
      }
    }
  }

  if (!enableFilter) return null

  const defaultTrigger = (
    <Button variant='outline' size='md' className={triggerClassName} data-active={triggerActive || undefined}>
      <div className='flex items-center'>
        <div className='flex items-center'>
          <Funnel className='w-4 h-4' />
          {appliedFilterCount != null && appliedFilterCount > 0 && (
            <span className='flex min-w-[20px] items-center justify-center rounded-full py-1 text-text-xs-semibold font-large text-#27272A'>
              {appliedFilterCount > 99 ? '99+' : appliedFilterCount}
            </span>
          )}
        </div>
        <span>{triggerLabel}</span>
      </div>
    </Button>
  )

  const menuTrigger =
    customTrigger != null && triggerTooltip != null ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{customTrigger}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {typeof triggerTooltip === 'string' ? <p>{triggerTooltip}</p> : triggerTooltip}
        </TooltipContent>
      </Tooltip>
    ) : (
      <PopoverTrigger asChild>{customTrigger ?? defaultTrigger}</PopoverTrigger>
    )

  const mainListView = (
    <>
      <div className='sticky top-0 z-10 w-full rounded-t-xl border-b'>
        <div className='bg-zinc-50 px-1'>
          <Input
            key='filter-dropdown-main-search'
            ref={mainListSearchInputRef}
            autoFocus
            placeholder='Search...'
            dimension='md'
            variant='none'
            className='h-[40px] bg-transparent px-2 !justify-start [&>input]:!bg-transparent rounded-none !overflow-hidden'
            startAdornment={<MagnifyingGlass className='text-zinc-500' size={20} />}
            value={filterSearchText}
            onChange={e => setFilterSearchText(e.target.value)}
            onKeyDown={handleMainListSearchKeyDown}
          />
        </div>
      </div>

      <div
        ref={mainFilterListRefCallback}
        onPointerEnter={handleMainFilterListPointerEnter}
        onKeyDown={handleMainFilterListKeyDown}
        className={`${MAIN_FILTER_LIST_SCROLL} flex max-h-[400px] flex-col overflow-y-auto outline-none`}
      >
        {filteredFilterConfig.length > 0 ? (
          <div className='flex flex-col p-1.5'>
            {filteredFilterConfig.map(([filterType, filter]) => (
              <button
                key={String(filterType)}
                type='button'
                data-main-filter-category-row
                onClick={() => handleCategoryClick(filterType)}
                className={MAIN_FILTER_CATEGORY_BTN}
              >
                <span className={MAIN_FILTER_CATEGORY_LABEL}>{filter.label}</span>
                <Icon
                  IconComponent={CaretRight}
                  size='xxxs'
                  className='text-zinc-400'
                  iconProps={{ weight: 'bold' }}
                />
              </button>
            ))}
          </div>
        ) : (
          <div className='p-4 text-center text-text-sm-regular text-zinc-400'>No filters found</div>
        )}
      </div>
    </>
  )

  const rangeSubMenuView = (
    <>
      <div className='sticky top-0 z-10 flex w-full shrink-0 border-b bg-zinc-50'>
        <div className='flex shrink-0 items-center px-3 py-2'>
          <LinkButton
            size='xs'
            className='!justify-start'
            leftIcon={
              <Icon
                IconComponent={CaretLeft}
                size='xxs'
                className='text-zinc-500'
                iconProps={{ weight: 'bold' }}
              />
            }
            href='#'
            onClick={e => {
              e.preventDefault()
              handleBackClick()
            }}
          >
            {activeSubMenuConfig?.label}
          </LinkButton>
        </div>
      </div>

      <div className='p-2'>
        {activeSubMenuConfig?.rangeInputMode === 'select' ? (
          <div className='flex gap-3 items-end p-2'>
            <div className='flex-1'>
              <label className='block text-text-xs-medium text-zinc-500 mb-1'>Type</label>
              <Select
                value={(rangeSelectedField[String(activeSubMenu)] ?? 'minPrice') as string}
                onValueChange={value => handleRangeSelectFieldChange(activeSubMenu!, value)}
              >
                <SelectTrigger className='w-full h-9 bg-white gap-2 rounded-lg border-zinc-200'>
                  <SelectValue placeholder='Select' />
                </SelectTrigger>
                <SelectContent>
                  {(activeSubMenuConfig?.rangeSelectOptions ?? [
                    { label: 'Min', value: 'minPrice' as const },
                    { label: 'Max', value: 'maxPrice' as const },
                  ]).map(opt => (
                    <SelectItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='flex-1'>
              <label className='block text-text-xs-medium text-zinc-500 mb-1'>Value</label>
              <div className='relative'>
                <span className='absolute left-3 top-1/2 -translate-y-1/2 text-text-sm-regular text-zinc-500'>
                  {currencySymbol}
                </span>
                <Input
                  type='number'
                  placeholder='Type value'
                  value={
                    activeRangeValue[rangeSelectedField[String(activeSubMenu)] ?? 'minPrice']
                  }
                  onChange={e =>
                    handleRangeFieldChange(
                      activeSubMenu!,
                      rangeSelectedField[String(activeSubMenu)] ?? 'minPrice',
                      e.target.value
                    )
                  }
                  className='h-9 pl-8'
                  dimension='md'
                  variant='outline'
                />
              </div>
            </div>
          </div>
        ) : (
          <div className='flex gap-2'>
            <div className='flex-1'>
              <label className='block text-text-xs-medium text-zinc-500 mb-1'>From</label>
              <div className='relative'>
                <span className='absolute left-3 top-1/2 -translate-y-1/2 text-text-sm-regular text-zinc-500'>
                  {currencySymbol}
                </span>
                <Input
                  type='number'
                  placeholder='e.g. 100'
                  value={activeRangeValue.minPrice}
                  onChange={e => handleRangeFieldChange(activeSubMenu!, 'minPrice', e.target.value)}
                  className='h-9 pl-8'
                  dimension='md'
                  variant='outline'
                />
              </div>
            </div>

            <div className='flex-1'>
              <label className='block text-text-xs-medium text-zinc-500 mb-1'>To</label>
              <div className='relative'>
                <span className='absolute left-3 top-1/2 -translate-y-1/2 text-text-sm-regular text-zinc-500'>
                  {currencySymbol}
                </span>
                <Input
                  type='number'
                  placeholder='e.g. 200'
                  value={activeRangeValue.maxPrice}
                  onChange={e => handleRangeFieldChange(activeSubMenu!, 'maxPrice', e.target.value)}
                  className='h-9 pl-8'
                  dimension='md'
                  variant='outline'
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {rangeValidationError ? (
        <p className='px-3 pt-1 text-text-xs-regular text-red-700' role='alert'>
          {rangeValidationError}
        </p>
      ) : null}
    </>
  )

  const searchSubMenuView = (
    <div className='p-3 space-y-3'>
      <div>
        <label className='block text-text-xs-medium text-zinc-500 mb-1'>Select Parameter</label>
        <Select
          value={String(activeSearchFilterValue.selectedOption ?? '')}
          onValueChange={value => handleSearchOptionChange(activeSubMenu!, value)}
        >
          <SelectTrigger className='w-full h-9 bg-white gap-2 rounded-lg border-zinc-200'>
            <SelectValue placeholder='Select UTM parameter' />
          </SelectTrigger>
          <SelectContent>
            {(activeSubMenuConfig?.options ?? []).map(option => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className='block text-text-xs-medium text-zinc-500 mb-1'>Search Value</label>
        <Input
          placeholder={activeSubMenuConfig?.searchPlaceholder ?? 'Enter search text...'}
          value={activeSearchFilterValue.searchText}
          onChange={e => handleSearchTextChange(activeSubMenu!, e.target.value)}
          className='h-9'
          dimension='md'
          variant='outline'
        />
      </div>
    </div>
  )

  const useOptionsApplyPending = Boolean(onOptionsApply)
  const optionsSubMenuKey = activeSubMenu != null ? String(activeSubMenu) : ''
  const selectedCountInFilteredSubMenu =
    activeSubMenu != null
      ? filteredSubMenuOptions.filter(opt =>
          useOptionsApplyPending
            ? (effectivePendingSelections[optionsSubMenuKey]?.has(String(opt.value)) ?? false)
            : (isOptionChecked?.(activeSubMenu, opt.value) ?? false)
        ).length
      : 0
  const allFilteredOptionsSelected =
    filteredSubMenuOptions.length > 0 &&
    selectedCountInFilteredSubMenu === filteredSubMenuOptions.length
  const someFilteredOptionsSelected =
    selectedCountInFilteredSubMenu > 0 && !allFilteredOptionsSelected

  const optionsSubMenuView = (
    <div className='flex w-full flex-col'>
      <div className='sticky top-0 z-10 flex w-full shrink-0 flex-col border-b bg-zinc-50'>
        <div className='flex shrink-0 items-center px-3 pt-2'>
          <LinkButton
            size='xs'
            className='!justify-start'
            leftIcon={
              <Icon
                IconComponent={CaretLeft}
                size='xxs'
                className='text-zinc-500'
                iconProps={{ weight: 'bold' }}
              />
            }
            href='#'
            onClick={e => {
              e.preventDefault()
              handleBackClick()
            }}
          >
            {activeSubMenuConfig?.label}
          </LinkButton>
        </div>
        <div className='shrink-0'>
          <div className='bg-zinc-50 px-1'>
            <Input
              key={`filter-dropdown-submenu-options-search-${String(activeSubMenu)}`}
              ref={subMenuSearchInputRef}
              autoFocus
              placeholder='Search...'
              dimension='md'
              variant='none'
              className='h-[36px] bg-transparent px-2 !justify-start [&>input]:!bg-transparent rounded-none !overflow-hidden'
              startAdornment={<MagnifyingGlass className='text-zinc-500' size={20} />}
              value={subMenuSearchText}
              onChange={e => setSubMenuSearchText(e.target.value)}
              onKeyDown={handleSubmenuSearchKeyDown}
            />
          </div>
        </div>
      </div>

      <div
        key={String(activeSubMenu)}
        ref={subMenuListRefCallback}
        onScroll={handleSubMenuScroll}
        onPointerEnter={handleOptionsListPointerEnter}
        onKeyDown={handleOptionsListKeyDown}
        className={`${SUBMENU_OPTIONS_SCROLL} flex max-h-[400px] min-h-0 w-full flex-1 flex-col overflow-y-auto outline-none`}
      >
        {filteredSubMenuOptions.length > 0 ? (
          <div className='flex flex-col p-1.5'>
            <button
              type='button'
              data-submenu-option-row
              role='checkbox'
              aria-checked={
                allFilteredOptionsSelected
                  ? true
                  : someFilteredOptionsSelected
                    ? 'mixed'
                    : false
              }
              className={SUBMENU_OPTION_ROW_BTN}
              onClick={e => {
                e.stopPropagation()
                toggleSelectAllFilteredOptions(activeSubMenu!)
              }}
            >
              <CheckboxV2
                className='pointer-events-none shrink-0'
                checked={allFilteredOptionsSelected}
                indeterminate={someFilteredOptionsSelected}
                size='sm'
                tabIndex={-1}
                aria-hidden
              />
              <span className={SUBMENU_OPTION_ROW_LABEL_SELECT_ALL}>
                Select all
              </span>
            </button>
            {filteredSubMenuOptions.map(option => {
              const checked = useOptionsApplyPending
                ? (effectivePendingSelections[optionsSubMenuKey]?.has(String(option.value)) ?? false)
                : (isOptionChecked?.(activeSubMenu!, option.value) ?? false)

              const handleRowActivate = () => {
                if (useOptionsApplyPending) {
                  togglePendingOption(activeSubMenu!, option.value)
                } else {
                  onOptionToggle?.(activeSubMenu!, option.value)
                }
              }

              return (
                <button
                  key={String(option.value)}
                  type='button'
                  data-submenu-option-row
                  role='checkbox'
                  aria-checked={checked}
                  className={SUBMENU_OPTION_ROW_BTN}
                  onClick={e => {
                    e.stopPropagation()
                    handleRowActivate()
                  }}
                >
                  <CheckboxV2
                    className='pointer-events-none shrink-0'
                    checked={checked}
                    size='sm'
                    tabIndex={-1}
                    aria-hidden
                  />
                  {option.dotColor ? (
                    <span
                      className='h-2 w-2 shrink-0 rounded-full'
                      style={{ backgroundColor: option.dotColor }}
                      aria-hidden
                    />
                  ) : null}
                  <span className={SUBMENU_OPTION_ROW_LABEL_OPTION}>
                    {option.label}
                  </span>
                  {option.count !== undefined && option.count !== null ? (
                    <span className='tabular-nums text-text-xs-medium text-zinc-500'>
                      {option.count}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : (
          <div className='p-4 text-center text-text-sm-regular text-zinc-400'>No options found</div>
        )}
      </div>
    </div>
  )

  const subMenuView = () => {
    if (activeSubMenuConfig?.isRangeFilter) return rangeSubMenuView
    if (activeSubMenuConfig?.isSearchFilter) return searchSubMenuView
    return optionsSubMenuView
  }

  /** Fade only for the checkbox list: it's the only sub-menu with a scroll container that drives `isListAtBottom`. */
  const showApplyScrollFade =
    activeSubMenu != null &&
    activeSubMenuConfig != null &&
    !activeSubMenuConfig.isRangeFilter &&
    !activeSubMenuConfig.isSearchFilter &&
    !isListAtBottom

  const applyButton = (
    <div className='relative px-1.5 pb-1.5'>
      {showApplyScrollFade ? (
        <div className='pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-b from-transparent to-white' />
      ) : null}
      <Button
        variant='outline'
        size='sm'
        className='w-full flex flex-row items-center justify-center gap-2 p-1'
        disabled={isApplying}
        onClick={handleApply}
      >
        <div className='flex flex-row items-center justify-center gap-1'>
          {isApplying ? (
            <>
              <CircleNotch className='w-4 h-4 animate-spin' weight='bold' />
              <span>Applying…</span>
            </>
          ) : (
            <span>Apply</span>
          )}
        </div>
      </Button>
    </div>
  )

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {menuTrigger}
      <PopoverContent
        align={mainMenuAlign}
        sideOffset={2}
        onOpenAutoFocus={e => {
          const isMainList = activeSubMenu == null
          const isOptionsSubmenu =
            activeSubMenu != null &&
            activeSubMenuConfig != null &&
            !activeSubMenuConfig.isRangeFilter &&
            !activeSubMenuConfig.isSearchFilter
          if (isMainList || isOptionsSubmenu) e.preventDefault()
        }}
        // Fire deferred apply callbacks now that the exit animation has fully completed.
        // Also prevent Radix Tooltip re-opening on focus when a tooltip trigger is used.
        onCloseAutoFocus={e => {
          flushPendingApply()
          if (triggerTooltip != null) e.preventDefault()
        }}
        className='!w-[240px] overflow-hidden rounded-xl border-0 bg-white p-0 !shadow-[0_0_0_0.5px_rgba(9,9,11,0.12),0_16px_16px_-6px_rgba(9,9,11,0.12),0_2px_10px_-6px_rgba(9,9,11,0.48)]'
      >
        {!activeSubMenu ? mainListView : (
          <>
            {subMenuView()}
            {applyButton}
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}